// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/StaffDetailsModal.js
import React from 'react';
import Modal from './Modal';
import { Mail, Phone, Shield, Calendar, IdCard, ShieldCheck, ShieldAlert } from 'lucide-react';

const StaffDetailsModal = ({ open, staffMember, onClose, onStatusToggle }) => {
  if (!open || !staffMember) return null;

  const fullName = `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim();
  const initials = fullName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const displayRole = staffMember.role 
    ? staffMember.role.charAt(0).toUpperCase() + staffMember.role.slice(1).toLowerCase() 
    : 'N/A';

  return (
    <Modal title="Staff Details" onClose={onClose}>
      <div className="space-y-5 p-0.5">
        
        {/* Profile Card Summary Banner */}
        <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
          <div className="h-12 w-12 shrink-0 bg-slate-100 border border-slate-200 rounded-xl flex items-center justify-center">
            <span className="text-slate-700 font-bold text-sm tracking-wide">{initials}</span>
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <h2 className="text-sm font-bold text-slate-900 truncate">{fullName}</h2>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                staffMember.status === 'active'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                  : 'bg-slate-100 text-slate-500 border border-slate-200'
              }`}>
                {staffMember.status === 'active' ? 'Active' : 'Disabled'}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                {displayRole}
              </span>
            </div>
          </div>
        </div>

        {/* Informational Data Info Row Items */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="space-y-3">
            <div className="flex gap-2.5 items-start">
              <Mail size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <label className="block font-semibold text-slate-400 mb-0.5">Email Address</label>
                <p className="text-slate-800 font-medium truncate">{staffMember.email || 'N/A'}</p>
              </div>
            </div>

            <div className="flex gap-2.5 items-start">
              <Phone size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div>
                <label className="block font-semibold text-slate-400 mb-0.5">Phone Number</label>
                <p className="text-slate-800 font-medium">{staffMember.phoneNumber || 'N/A'}</p>
              </div>
            </div>

            <div className="flex gap-2.5 items-start">
              <IdCard size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <label className="block font-semibold text-slate-400 mb-0.5">User ID Code</label>
                <p className="text-slate-700 font-mono font-bold truncate select-all">{staffMember.userId || staffMember._id || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex gap-2.5 items-start">
              <Shield size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div>
                <label className="block font-semibold text-slate-400 mb-0.5">System Privilege Role</label>
                <p className="text-slate-800 font-medium">{displayRole}</p>
              </div>
            </div>

            <div className="flex gap-2.5 items-start">
              {staffMember.status === 'active' ? (
                <ShieldCheck size={14} className="text-emerald-500 mt-0.5 shrink-0" />
              ) : (
                <ShieldAlert size={14} className="text-slate-400 mt-0.5 shrink-0" />
              )}
              <div>
                <label className="block font-semibold text-slate-400 mb-0.5">System Access Status</label>
                <p className={`font-bold ${staffMember.status === 'active' ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {staffMember.status === 'active' ? 'Authorized' : 'Suspended'}
                </p>
              </div>
            </div>

            <div className="flex gap-2.5 items-start">
              <Calendar size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div>
                <label className="block font-semibold text-slate-400 mb-0.5">Enrollment Date</label>
                <p className="text-slate-800 font-medium">
                  {staffMember.createdAt ? new Date(staffMember.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Department Information Panel Blocks */}
        {(staffMember.department || staffMember.designation) && (
          <div className="border-t border-slate-100 pt-3.5 mt-1">
            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">Campus Assignments</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-slate-50 p-3 rounded-xl border border-slate-200/40">
              {staffMember.department && (
                <div>
                  <label className="block font-semibold text-slate-400 mb-0.5">Department Allocation</label>
                  <p className="text-slate-800 font-bold">{staffMember.department}</p>
                </div>
              )}
              {staffMember.designation && (
                <div>
                  <label className="block font-semibold text-slate-400 mb-0.5">Official Designation</label>
                  <p className="text-slate-800 font-bold">{staffMember.designation}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Window Close & Control Callout Actions Footer Panel */}
        <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center gap-2 pt-3 border-t border-slate-100 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="h-10 sm:h-9 px-4 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors"
          >
            Close
          </button>
          
          <button
            type="button"
            onClick={() => onStatusToggle(staffMember.userId || staffMember._id, staffMember.status)}
            className={`h-10 sm:h-9 px-4 text-xs font-bold rounded-lg transition-colors ${
              staffMember.status === 'active'
                ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200/60'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
            }`}
          >
            {staffMember.status === 'active' ? 'Disable Access' : 'Enable Access'}
          </button>
        </div>

      </div>
    </Modal>
  );
};

export default StaffDetailsModal;